import React, { useState, useEffect, useContext } from "react";
import { createPortal } from "react-dom";
import { X, CheckCircle, Database } from "lucide-react";
import { motion, useDragControls, useMotionValue } from "motion/react";
import useConfig from "../../demos/hooks/useConfig";
import { CadviewerContext } from "../../context/Cadviewer.Context";

interface DataContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (metric: string) => void;
  initialMetric: string;
}

const EXCLUDED_KEYS = ["Mall Name", "Property #", "DBA", "Space #", "GLA"];

export const DataContentModal: React.FC<DataContentModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialMetric
}) => {
  const { config } = useConfig();
  const { user } = useContext(CadviewerContext);
  const [metrics, setMetrics] = useState<string[]>([]);
  const [selectedMetric, setSelectedMetric] = useState<string>(initialMetric);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  const dragControls = useDragControls();
  const y = useMotionValue(0);

  const clipUrl = (fullUrl: string) => {
    let path = "";
    try {
      const urlObj = new URL(fullUrl);
      path = urlObj.pathname;
    } catch {
      path = fullUrl;
    }
    const contentIndex = path.indexOf("/content/");
    if (contentIndex !== -1) {
      path = path.substring(contentIndex + "/content/".length);
    }
    if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
    return path;
  };

  const findFolderByPath = (folderList: any[], targetPath: string): any | null => {
    const normalizedTarget = targetPath.replace(/\\/g, '/').toLowerCase();
    
    for (const folder of folderList) {
      if (folder.path) {
        const normalizedFolder = folder.path.replace(/\\/g, '/').toLowerCase();
        if (normalizedFolder.endsWith(normalizedTarget) || normalizedFolder.endsWith(normalizedTarget + '/')) {
          return folder;
        }
      }
      if (folder.children) {
        const found = findFolderByPath(folder.children, targetPath);
        if (found) return found;
      }
    }
    return null;
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedMetric(initialMetric);
      if (metrics.length === 0) {
        fetchMetrics();
      }
    }
  }, [isOpen, initialMetric]);

  const fetchMetrics = async () => {
    setLoading(true);
    setError(null);
    try {
      let endpoint = "listdwgdirectory";
      const backendUrl = config.ServerBackEndUrl || "http://localhost:3000";
      if (!backendUrl.endsWith("/")) endpoint = "/" + endpoint;
      const headers = new Headers();
      if (user?.token && config.enableLoginFeature) {
        headers.append("Authorization", `Bearer ${user.token}`);
      }
      headers.append("Content-Type", "application/json");

      const response = await fetch(
        backendUrl + endpoint + `?loginFeatureEnabled=${config.enableLoginFeature}`,
        { method: "GET", headers }
      );
      
      if (!response.ok) throw new Error("Failed to load folder structure from server");
      const folderStructure = await response.json();

      const jsonRelativePath = clipUrl(config.propertyJSONFilesLocation);
      const jsonFolder = findFolderByPath(folderStructure, jsonRelativePath);

      if (!jsonFolder || !jsonFolder.children) {
        throw new Error(`Could not find JSON directory matching: ${jsonRelativePath}`);
      }

      const jsonFile = jsonFolder.children.find((c: any) => 
        !c.isFolder && c.name && c.name.endsWith(".json") && !c.name.endsWith("-distances.json")
      );

      if (!jsonFile) {
        throw new Error("No valid property JSON files found in the directory.");
      }

      let jsonBaseUrl = config.propertyJSONFilesLocation;
      if (jsonFolder.path) {
        const normJsonPath = jsonFolder.path.replace(/\\/g, '/');
        const contentIdx = normJsonPath.indexOf("/content/");
        if (contentIdx !== -1) {
          const relativeToContent = normJsonPath.substring(contentIdx);
          jsonBaseUrl = backendUrl + (backendUrl.endsWith("/") ? relativeToContent.substring(1) : relativeToContent);
        }
      }
      if (!jsonBaseUrl.endsWith("/")) jsonBaseUrl += "/";
      
      const fileRes = await fetch(jsonBaseUrl + jsonFile.name);
      if (!fileRes.ok) throw new Error("Failed to load the JSON file.");
      
      const fileData = await fileRes.json();
      
      if (fileData?.data?.spaces?.length > 0) {
        const firstSpace = fileData.data.spaces[0];
        if (firstSpace.detailTabs?.length > 0) {
          const detail = firstSpace.detailTabs[0].detail;
          if (detail) {
            const allKeys = Object.keys(detail);
            const filteredKeys = allKeys.filter(k => !EXCLUDED_KEYS.includes(k));
            setMetrics(filteredKeys);
            setLoading(false);
            return;
          }
        }
      }
      throw new Error("Invalid or empty property JSON structure.");
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Failed to fetch data content.");
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center pointer-events-none" style={{ zIndex: 1000000010 }}>
      <motion.div
        className="w-[400px] bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-200 pointer-events-auto"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        style={{ y }}
        drag="y"
        dragControls={dragControls}
        dragListener={false}
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={0}
      >
        <div 
          className="flex justify-between items-center px-4 py-3 bg-slate-50 border-b border-gray-200 cursor-move"
          onPointerDown={(e) => dragControls.start(e)}
        >
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-indigo-500" />
            <h2 className="text-sm font-semibold text-gray-700">Select Data Content</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 max-h-[300px] overflow-y-auto">
          {loading ? (
            <div className="text-sm text-gray-500 text-center py-4">Loading JSON structure...</div>
          ) : error ? (
            <div className="text-sm text-red-500 text-center py-4">{error}</div>
          ) : metrics.length === 0 ? (
            <div className="text-sm text-gray-500 text-center py-4">No metrics available.</div>
          ) : (
            <div className="space-y-2">
              {metrics.map((metric) => (
                <label 
                  key={metric}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedMetric === metric ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="metricSelection"
                    value={metric}
                    checked={selectedMetric === metric}
                    onChange={() => setSelectedMetric(metric)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">{metric}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onSave(selectedMetric);
              onClose();
            }}
            disabled={!selectedMetric}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors ${
              selectedMetric ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            <CheckCircle size={16} />
            Save Selection
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
};
